import React from 'react';
import { Card } from '../../ui/card';
import { cn } from '../../../lib/utils';
import { formatAmount, depositTotals } from './format';
import { wageringSummary, playerTagViews } from './wagering';
import { exceptionItems } from './playerSummary';
import WageringRequirement, { PlayerTag } from './WageringRequirement';
import PlayerIdentity from './PlayerIdentity';

const TONES = { positive: 'text-positive', warning: 'text-warning', negative: 'text-negative', muted: 'text-ink-dim' };

function Tile({ label, children }) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5 rounded-lg border border-white/10 bg-surface-2/50 px-4 py-3">
      <span className="text-xs font-medium text-ink-mute">{label}</span>
      {children}
    </div>
  );
}

// Deposited: deposits since the start point per currency, then their USD sum when currencies
// are combined (crypto or more than one currency).
function Deposited({ deposits }) {
  const { lines, combinedUSD } = depositTotals(deposits);
  if (lines.length === 0) return <span className="text-lg font-bold text-ink">—</span>;
  return (
    <div className="flex flex-col gap-0.5">
      {lines.map((l) => <span key={l.currency} className="text-lg font-bold text-ink">{formatAmount(l.amount, l.currency)}</span>)}
      {combinedUSD && <span className="text-xs font-bold text-cta">= {formatAmount(combinedUSD, 'USD')} combined</span>}
    </div>
  );
}

function Exceptions({ items }) {
  if (items.length === 0) return <span className="text-lg font-bold text-ink-dim">None</span>;
  return (
    <ul className="flex flex-col gap-1">
      {items.map((e) => (
        <li key={e.key} className="flex flex-col">
          <span className={cn('text-sm font-bold', TONES[e.tone])}>{e.label}</span>
          <span className="text-xs font-medium text-ink-mute">{e.detail}</span>
        </li>
      ))}
    </ul>
  );
}

// PlayerTags: the player's tags, VIP wagering and CC exception tags first and coloured.
function PlayerTags({ tags, view }) {
  if (!tags?.length) return null;
  return (
    <div>
      <div className="mb-1.5 text-xs font-medium text-ink-mute">Tags</div>
      <div className="flex flex-wrap gap-1.5">
        {playerTagViews(tags, view).map((t) => <PlayerTag key={t.tag} tag={t.tag} role={t.role} />)}
      </div>
    </div>
  );
}

// BonusPlayerCard sums the player up for a comp request (detailed view): wagering, deposits since
// the start point, the exceptions in play, and the tags behind them. The verdict is in the result card.
export default function BonusPlayerCard({ check, exception, backofficeUrl }) {
  const { player, deposits } = check;
  const wageringView = wageringSummary(check.wagering);
  return (
    <Card className="space-y-4 px-5 py-3.5">
      <PlayerIdentity player={player} backofficeUrl={backofficeUrl} />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Tile label="Wagering requirement"><WageringRequirement view={wageringView} label={false} /></Tile>
        <Tile label="Deposited since start point"><Deposited deposits={deposits} /></Tile>
        <Tile label="Exceptions"><Exceptions items={exceptionItems(check, exception)} /></Tile>
      </div>

      <PlayerTags tags={player.tags} view={wageringView} />
    </Card>
  );
}
